import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { listProducts } from '../api/products';
import { useCart } from '../contexts/CartContext';
import Rack, { RACK_COLORS as COLORS } from '../components/Rack';

const ROW_HEIGHT = 3.4;

const PRESETS = [
  { id: 'single', label: 'Single Blue', dim: '1×5', rows: [{ color: 'blue' }] },
  { id: 'double', label: 'Double Stack', dim: '2×5', rows: [{ color: 'blue' }, { color: 'red' }] },
  { id: '3x5', label: '3×5 Modular', dim: '15 balls', rows: [{ color: 'blue' }, { color: 'white' }, { color: 'red' }] },
  { id: '4x5', label: '4×5 Modular', dim: '20 balls', rows: [{ color: 'blue' }, { color: 'red' }, { color: 'white' }, { color: 'blue' }] },
  { id: '5x5min', label: '5×5 Minimalist', dim: '25 balls', rows: [{ color: 'ink' }, { color: 'ink' }, { color: 'ink' }, { color: 'ink' }, { color: 'ink' }] },
  { id: '5x5rwb', label: '5×5 R-W-B', dim: '15 balls', rows: [{ color: 'blue' }, { color: 'white' }, { color: 'red' }, { color: 'blue' }, { color: 'white' }] },
];

function rowsEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((r, i) => r.color === b[i].color);
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const { addItem } = useCart();

  const [rows, setRows] = useState([{ color: 'blue' }]);
  const [paint, setPaint] = useState('blue');

  useEffect(() => {
    listProducts().then(res => setProducts(res.data)).catch(() => {});
  }, []);

  const revealRef = useRef([]);
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [rows.length]);

  const addRow = useCallback(() => {
    setRows(prev => prev.length >= 5 ? prev : [...prev, { color: paint }]);
  }, [paint]);
  const removeRow = useCallback(() => {
    setRows(prev => prev.length <= 1 ? prev : prev.slice(0, -1));
  }, []);
  const recolorRow = useCallback((i) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { color: paint } : r));
  }, [paint]);

  const ballCount = rows.reduce((n, r) => n + (r.color === 'white' ? 0 : 5), 0);
  const totalPrice = 1800 + (rows.length - 1) * 1400;
  const rackHeight = (3.0 + rows.length * ROW_HEIGHT).toFixed(1);
  const activePreset = PRESETS.find(p => rowsEqual(p.rows, rows))?.id;

  const single = products.find(p => p.slug?.includes('single'));
  const trophy = products.find(p => p.slug?.includes('trophy'));
  const backNine = products.find(p => p.slug?.includes('back'));

  const handleShopCardAdd = async (product) => {
    if (!product) return;
    try { await addItem({ product_id: product.id, quantity: 1 }); } catch {}
  };

  const [openFaq, setOpenFaq] = useState(null);
  const faqs = [
    { q: 'How many balls does each module hold?', a: 'Each module holds exactly five golf balls in a single row. The concave slots hold them securely — no rattling, no rolling.' },
    { q: 'Can I stack multiple modules?', a: "Yes — that's the whole point. Modules stack vertically with tongue-and-groove joinery, up to 5 high in a single rack." },
    { q: 'What colors are available?', a: 'Production colors are Navy Blue, Crimson Red, and White. Brand-extension colors (Cream, Ink, Gold, Forest) ship in 2–3 weeks.' },
    { q: 'How do I mount them on the wall?', a: 'Two drywall anchors and screws ship in the box, with a printed level template. Two minutes. Add modules later without re-drilling.' },
    { q: 'What material are they made from?', a: "PETG filament, 3D-printed in Phoenix, AZ. It's rigid, weatherproof, impact-resistant, and looks great on the wall." },
    { q: 'What if my rack breaks?', a: "Lifetime rack guarantee. Snap one, email us a photo, we'll print a replacement in the same color and ship it free." },
  ];

  return (
    <>
      {/* ══════ HERO ══════ */}
      <section className="hero-editorial">
        <div className="hero-grid">
          <div>
            <div className="hero-eyebrow anim anim-d1">
              <span className="line" style={{ width: 48, height: 1, background: 'var(--gold)' }}></span>
              <span className="mono">Modular display racks</span>
            </div>
            <h1 className="display anim anim-d2">
              Every ball<br />
              has <em>a story.</em><br />
              Frame it.
            </h1>
            <p className="hero-sub anim anim-d3">
              Wall-mounted, modular golf ball display racks. Each module holds five balls and stacks up to five high. <strong>3D-printed in Phoenix</strong>, shipped to your wall.
            </p>
            <div className="hero-cta anim anim-d4">
              <a href="#build" className="btn-editorial">Build your rack <span style={{ transition: 'transform 0.3s' }}>&rarr;</span></a>
              <a href="#gallery" className="btn-ghost-ed">See the gallery</a>
            </div>
          </div>

          <div className="hero-image anim anim-d5">
            <div className="hero-photo">
              <span className="photo-tag">5×5 — R / W / B</span>
              <img src="/racks/5x5multicolor.png" alt="Wall-mounted 5x5 modular golf ball rack with red, white, and blue rows" />
            </div>
          </div>
        </div>

        <div className="hero-numeric anim anim-d5">
          <div><div className="num"><em>5</em> balls</div><div className="mono">per module</div></div>
          <div><div className="num"><em>5</em> high</div><div className="mono">max stack</div></div>
          <div><div className="num">3 colors</div><div className="mono">in production</div></div>
          <div><div className="num">$18</div><div className="mono">starting at</div></div>
          <div><div className="num"><em>&lt;</em>2 min</div><div className="mono">install time</div></div>
        </div>
      </section>

      <div className="divider"></div>

      {/* ══════ BUILD YOUR RACK ══════ */}
      <section id="build" style={{ padding: '120px 40px', position: 'relative' }}>
        <div className="builder">
          <div className="builder-viewport reveal">
            <div className="viewport-corner tl">Elevation A</div>
            <div className="viewport-corner br">H: {rackHeight}"</div>
            <div className="builder-stack">
              <Rack rows={rows} onRowClick={recolorRow} />
            </div>
          </div>

          <div className="builder-panel reveal">
            <div className="mono" style={{ marginBottom: 16 }}>Configure</div>
            <h3>Build your <em>rack.</em></h3>
            <p>
              Start with a preset or stack one row at a time. Pick a color, click any row to repaint it. White rows display empty for trophies-only.
            </p>

            <div className="mono" style={{ marginBottom: 12 }}>Presets</div>
            <div className="preset-row">
              {PRESETS.map(p => (
                <button
                  key={p.id}
                  className={`preset-btn ${activePreset === p.id ? 'active' : ''}`}
                  onClick={() => setRows(p.rows.map(r => ({ ...r })))}
                >
                  <span className="preset-label">{p.label}</span>
                  <span className="preset-dim">{p.dim}</span>
                </button>
              ))}
            </div>

            <div className="mono" style={{ marginBottom: 12 }}>Rows</div>
            <div className="counter-row">
              <button className="count-btn" onClick={removeRow}>&minus;</button>
              <div className="count-readout">
                <span className="mono">rows × 5</span>
                <span className="big"><em>{rows.length}</em></span>
              </div>
              <button className="count-btn" onClick={addRow}>+</button>
            </div>

            <div className="stat-row">
              <div>
                <div className="num"><em>{ballCount}</em></div>
                <div className="mono">balls</div>
              </div>
              <div>
                <div className="num"><em>{rackHeight}"</em></div>
                <div className="mono">height</div>
              </div>
              <div>
                <div className="num">$<em>{(totalPrice / 100).toFixed(0)}</em></div>
                <div className="mono">total</div>
              </div>
            </div>

            <div>
              <div className="mono" style={{ marginBottom: 12, color: 'var(--gold-dim)' }}>Paint color — click a row to apply</div>
              <div className="color-chips">
                {COLORS.map(c => (
                  <button
                    key={c}
                    className={`chip chip-${c} ${paint === c ? 'active' : ''}`}
                    onClick={() => setPaint(c)}
                    title={c}
                  />
                ))}
              </div>
            </div>

            <Link to="/shop" className="btn-editorial" style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}>
              Shop now <span>&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      {/* ══════ REAL RACKS GALLERY ══════ */}
      <section id="gallery" className="gallery-section">
        <div className="sec-head reveal">
          <div>
            <div className="eyebrow" style={{ marginBottom: 12 }}>In the wild</div>
            <h2>Real racks, <em>real walls.</em></h2>
          </div>
          <div className="mono" style={{ color: 'var(--gold-dim)' }}>Photographed in customer homes</div>
        </div>

        <div className="gallery-grid">
          <div className="gallery-card feature reveal">
            <img src="/racks/5x5multicolor.png" alt="5x5 multicolor rack" />
            <div className="gallery-meta"><em>5×5</em> — Patriot stack</div>
          </div>
          <div className="gallery-card reveal">
            <img src="/racks/5x5white.png" alt="5x5 navy rack" />
            <div className="gallery-meta"><em>5×5</em> — Minimalist navy</div>
          </div>
          <div className="gallery-card reveal">
            <img src="/racks/4x5multicolor.png" alt="4x5 multicolor rack" />
            <div className="gallery-meta"><em>4×5</em> — Modular</div>
          </div>
          <div className="gallery-card reveal">
            <img src="/racks/3x5multicolor.png" alt="3x5 multicolor rack" />
            <div className="gallery-meta"><em>3×5</em> — Modular</div>
          </div>
          <div className="gallery-card reveal">
            <img src="/racks/2x5multicolor.png" alt="2x5 double stack" />
            <div className="gallery-meta"><em>2×5</em> — Double stack</div>
          </div>
          <div className="gallery-card wide reveal">
            <img src="/racks/1x5blue.png" alt="1x5 single blue rack" />
            <div className="gallery-meta"><em>1×5</em> — Single blue</div>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      {/* ══════ PRODUCTS ══════ */}
      <section id="shop" style={{ padding: '120px 40px', background: 'var(--bg-2)', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 200, background: 'linear-gradient(180deg, var(--bg), var(--bg-2))', pointerEvents: 'none' }}></div>
        <div className="sec-head reveal" style={{ position: 'relative', zIndex: 1 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Catalog</div>
            <h2>The <em>collection.</em></h2>
          </div>
          <Link to="/shop" className="btn-ghost-ed">View all &rarr;</Link>
        </div>

        <div className="shop-grid" style={{ position: 'relative', zIndex: 1 }}>
          <div className="shop-card reveal">
            <div className="tag-pill">Starter</div>
            <h4>The <em>Single.</em></h4>
            <div className="sub">One module, your color, wall-ready.</div>
            <div className="shop-viz">
              <div className="stack-mini"><Rack rows={[{ color: 'blue' }]} compact /></div>
            </div>
            <ul>
              <li>1 &times; module <span className="mono">holds 05</span></li>
              <li>Wall anchors incl. <span className="mono">&times;2</span></li>
              <li>Choice of 3 colors <span className="mono">N/R/W</span></li>
            </ul>
            <div className="shop-price"><span className="p">$<em>18</em></span><span className="u">USD / shipped</span></div>
            <button className="btn-editorial" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleShopCardAdd(single)}>
              Add to cart &rarr;
            </button>
          </div>

          <div className="shop-card featured reveal">
            <div className="tag-pill">Most popular</div>
            <h4>The <em>Trophy Pair.</em></h4>
            <div className="sub">Two modules, tongue-and-groove stacked.</div>
            <div className="shop-viz">
              <div className="stack-mini">
                <Rack rows={[{ color: 'blue' }, { color: 'red' }]} compact />
              </div>
            </div>
            <ul>
              <li>2 &times; modules <span className="mono">holds 10</span></li>
              <li>Mixed-color option <span className="mono">3&sup2;</span></li>
              <li>Signed hanging card <span className="mono">incl.</span></li>
            </ul>
            <div className="shop-price"><span className="p">$<em>32</em></span><span className="u">USD / ships free</span></div>
            <button className="btn-editorial" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleShopCardAdd(trophy)}>
              Add to cart &rarr;
            </button>
          </div>

          <div className="shop-card reveal">
            <div className="tag-pill">Best value</div>
            <h4>The <em>Back-Nine.</em></h4>
            <div className="sub">Three modules, any combination.</div>
            <div className="shop-viz">
              <div className="stack-mini">
                <Rack rows={[{ color: 'blue' }, { color: 'white' }, { color: 'red' }]} compact />
              </div>
            </div>
            <ul>
              <li>3 &times; modules <span className="mono">holds 15</span></li>
              <li>Custom engraving <span className="mono">+$8</span></li>
              <li>Stack up to 5 high <span className="mono">5×5</span></li>
            </ul>
            <div className="shop-price"><span className="p">$<em>48</em></span><span className="u">USD / engravable</span></div>
            <button className="btn-editorial" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleShopCardAdd(backNine)}>
              Add to cart &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* ══════ ANATOMY ══════ */}
      <section id="anatomy" className="anatomy-section" style={{ padding: '120px 40px' }}>
        <div className="anatomy-inner">
          <div className="anatomy-copy reveal">
            <div className="mono" style={{ marginBottom: 16 }}>Engineering</div>
            <h3>Built to <em>last.</em></h3>
            <p>
              Every module is 3D-printed in solid PETG in our Phoenix workshop. Weatherproof, impact-resistant, and finished by hand before it ships.
            </p>
            <ol className="anatomy-list">
              <li>
                <span className="n">01</span>
                <span><span className="label">Tongue-and-groove joinery</span><br /><span style={{ color: 'var(--cream-dim)', fontSize: '0.85rem', fontWeight: 300 }}>Snaps flush, rated to 40 lbs shear</span></span>
              </li>
              <li>
                <span className="n">02</span>
                <span><span className="label">Wall-anchor mounting</span><br /><span style={{ color: 'var(--cream-dim)', fontSize: '0.85rem', fontWeight: 300 }}>Two drywall anchors, no re-drilling</span></span>
              </li>
              <li>
                <span className="n">03</span>
                <span><span className="label">Six colors, one system</span><br /><span style={{ color: 'var(--cream-dim)', fontSize: '0.85rem', fontWeight: 300 }}>Mix freely, seasonal drops coming</span></span>
              </li>
              <li>
                <span className="n">04</span>
                <span><span className="label">Lifetime guarantee</span><br /><span style={{ color: 'var(--cream-dim)', fontSize: '0.85rem', fontWeight: 300 }}>Break it, we replace it free</span></span>
              </li>
            </ol>
          </div>

          <div className="anatomy-viz reveal">
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Rack rows={[{ color: 'blue' }, { color: 'white' }, { color: 'red' }]} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(201,168,76,0.08)' }}>
              <div style={{ width: 32, height: 1, background: 'var(--gold-dim)' }}></div>
              <span className="mono" style={{ fontSize: '0.5rem', color: 'var(--gold-dim)' }}>Fig. 1 — 3×5 modular display, elevation A</span>
            </div>
          </div>
        </div>
      </section>

      <div className="divider"></div>

      {/* ══════ TESTIMONIAL ══════ */}
      <section style={{ padding: '120px 40px', background: 'var(--bg-2)' }}>
        <div className="testimonial reveal">
          <div className="mark">&ldquo;</div>
          <blockquote>
            I started with one module after my first hole-in-one. Six months later I've got a five-high stack and I'm running out of wall space. Best $70 I've spent on golf — and I've spent <em>a lot.</em>
          </blockquote>
          <cite>Mark T. — Scottsdale, AZ — 5 modules</cite>
        </div>
      </section>

      <div className="divider"></div>

      {/* ══════ FAQ ══════ */}
      <section id="faq" style={{ padding: '120px 40px' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }} className="reveal">
          <div className="mono" style={{ marginBottom: 16 }}>Support</div>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 300 }}>Common <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>questions.</em></h2>
        </div>

        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          {faqs.map((faq, i) => (
            <div
              key={i}
              style={{ borderBottom: '1px solid rgba(201,168,76,0.08)' }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{
                  width: '100%', textAlign: 'left', padding: '24px 0',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontFamily: 'var(--font-body)', fontSize: '1rem', fontWeight: 400,
                  color: 'var(--cream)', cursor: 'pointer', background: 'none', border: 'none',
                  transition: 'color 0.3s',
                }}
              >
                {faq.q}
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: '1.6rem', fontWeight: 300,
                  color: 'var(--gold-dim)', transition: 'transform 0.4s',
                  transform: openFaq === i ? 'rotate(45deg)' : 'none', lineHeight: 1,
                }}>+</span>
              </button>
              <div style={{
                maxHeight: openFaq === i ? 200 : 0, overflow: 'hidden',
                transition: 'max-height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding 0.5s',
                padding: openFaq === i ? '0 0 24px' : 0,
              }}>
                <p style={{ color: 'var(--cream-dim)', fontWeight: 300, lineHeight: 1.7, fontSize: '0.9rem' }}>{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════ CTA ══════ */}
      <section style={{ padding: '100px 40px', background: 'var(--bg-3)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 100% at 50% 100%, rgba(201,168,76,0.06) 0%, transparent 60%)', pointerEvents: 'none' }}></div>
        <div className="reveal" style={{ maxWidth: 600, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div className="mono" style={{ marginBottom: 16 }}>Get started</div>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 300, marginBottom: 16 }}>
            Start with <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>one.</em> Stack <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>five high.</em>
          </h2>
          <p style={{ color: 'var(--cream-dim)', fontWeight: 300, marginBottom: 36 }}>
            Every collection starts with a single module. Pick your color, hang it in two minutes, and add as your collection grows.
          </p>
          <Link to="/shop" className="btn-editorial">Shop the collection <span>&rarr;</span></Link>
        </div>
      </section>
    </>
  );
}
